import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import type { MapPin } from '../../utils/nearbyMapViewport';
import {
  googleZoomForRadiusMeters,
  leafletZoomBoundsForViewport,
  NEARBY_MAP_MAX_RADIUS_METERS,
  spanMetersForViewport,
} from '../../utils/nearbyMapViewport';

// Fix default marker paths in CRA bundles.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(L.Icon.Default as any).mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

type Props = {
  center: { lat: number; lng: number };
  pins: MapPin[];
  onViewportCenterChange?: (lat: number, lng: number) => void;
};

function boundsAround(center: { lat: number; lng: number }, radiusMeters: number): L.LatLngBounds {
  const latDelta = radiusMeters / 111_320;
  const lngDelta = radiusMeters / (111_320 * Math.cos((center.lat * Math.PI) / 180));
  return L.latLngBounds(
    [center.lat - latDelta, center.lng - lngDelta],
    [center.lat + latDelta, center.lng + lngDelta]
  );
}

export default function NearbyLeafletMap({ center, pins, onViewportCenterChange }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const viewportCenterRef = useRef(center);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: true,
    });
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);

    markersRef.current = L.layerGroup().addTo(map);

    const maxBounds = boundsAround(center, NEARBY_MAP_MAX_RADIUS_METERS);
    map.setMaxBounds(maxBounds);

    const clampView = () => {
      const c = map.getCenter();
      const vc = viewportCenterRef.current;
      const origin = L.latLng(vc.lat, vc.lng);
      if (origin.distanceTo(c) > NEARBY_MAP_MAX_RADIUS_METERS) {
        map.panTo(origin, { animate: true });
      }
      const { minZoom, maxZoom } = leafletZoomBoundsForViewport(c.lat);
      const z = map.getZoom();
      if (z < minZoom) map.setZoom(minZoom);
      if (z > maxZoom) map.setZoom(maxZoom);
    };

    map.on('zoomend', clampView);
    map.on('moveend', clampView);

    return () => {
      map.off('zoomend', clampView);
      map.off('moveend', clampView);
      map.remove();
      mapRef.current = null;
      markersRef.current = null;
    };
  }, [center.lat, center.lng]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = markersRef.current;
    if (!map || !layer) return;

    viewportCenterRef.current = center;
    const maxBounds = boundsAround(center, NEARBY_MAP_MAX_RADIUS_METERS);
    map.setMaxBounds(maxBounds);

    layer.clearLayers();
    for (const pin of pins) {
      L.marker([pin.lat, pin.lng]).bindTooltip(pin.name, { direction: 'top' }).addTo(layer);
    }

    const spanMeters = spanMetersForViewport(center, pins);
    const zoom = googleZoomForRadiusMeters(spanMeters / 2);
    map.setView([center.lat, center.lng], zoom, { animate: false });

    const { minZoom, maxZoom } = leafletZoomBoundsForViewport(center.lat);
    map.setMinZoom(minZoom);
    map.setMaxZoom(maxZoom);

    onViewportCenterChange?.(center.lat, center.lng);
  }, [center.lat, center.lng, pins, onViewportCenterChange]);

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', minHeight: 320 }}
      data-testid="nearby-leaflet-map"
    />
  );
}
